import {v4 as uuidv4} from 'uuid';
import {createMessage, encrypt as pgpEncrypt, readKey} from 'openpgp';
import {CreateCardPayload, CreatePaymentPayload, PublicKey} from './types';

// eslint-disable-next-line no-shadow
export enum PaymentStatus {
  CONFIRMED = 'confirmed',
  FAILED = 'failed',
  ACTION_REQUIRED = 'action required',
}

const encrypt: (
  dataToEncrypt: {
    number: string;
    cvv: string;
  },
  {keyId, publicKey}: PublicKey,
) => Promise<{
  encryptedMessage: string;
  keyId: string;
}> = async (dataToEncrypt, {keyId, publicKey}) => {
  if (!publicKey || !keyId) {
    throw new Error('Unable to encrypt data');
  }

  // atob / btoa incorrectly marked as deprecated https://github.com/microsoft/TypeScript/issues/45566
  const decodedPublicKey = await readKey({armoredKey: atob(publicKey)});

  const message = await createMessage({text: JSON.stringify(dataToEncrypt)});
  return pgpEncrypt({
    message,
    encryptionKeys: decodedPublicKey,
  }).then((ciphertext: any) => {
    return {
      encryptedMessage: btoa(ciphertext),
      keyId,
    };
  });
};

export class CirclePay {
  private getEncryptionPublicKey: () => Promise<{
    keyId: string;
    publicKey: string;
  }> = async () => {
    return this.sendRequest('/v1/encryption/public', {method: 'GET'});
  };

  createCard: (body: CreateCardPayload) => Promise<any> = (body) => {
    return this.sendRequest('/v1/cards', {
      body: JSON.stringify(body),
      method: 'POST',
    });
  };

  createPayment: (body: CreatePaymentPayload) => Promise<any> = (body) => {
    return this.sendRequest('/v1/payments', {
      body: JSON.stringify(body),
      method: 'POST',
    });
  };

  generateIdempotencyKey: () => string = () => {
    return uuidv4();
  };

  sessionId = uuidv4();

  encryptCardDetails: (cardDetails: {number: string; cvv: string}) => Promise<{
    encryptedMessage: string;
    keyId: string;
  }> = async (cardDetails) => {
    const publicKey = await this.getEncryptionPublicKey();
    return encrypt(cardDetails, publicKey);
  };

  getPayment: (paymentId: string) => Promise<any> = async (paymentId) => {
    return this.sendRequest(`/v1/payments/${paymentId}`, {method: 'GET'});
  };

  private sendRequest: (
    path: string,
    {
      method,
      body,
    }: {
      method: 'GET' | 'POST';
      body?: any;
    },
  ) => Promise<any> = async (path, {method, body}) => {
    const response = await fetch('/api/circle-pay', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        path,
        method,
        body,
      }),
    }).then(async (res) => {
      const data = await res.json();
      if (res.status >= 400) {
        throw new Error(data.message);
      }
      return data;
    });

    const {data} = response;
    return data;
  };
}
